import { Button, Icons, Tooltip, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const PanDocumentUploadTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.CorporatePAN
  );

  const handleViewFile = () => viewUploadedFile(file.id);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads PAN document" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="PAN document uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={uploadModalVisibility.show}
                  variant="primary"
                >
                  <Icons.Upload01 className="size-4" />
                  Upload
                </Button>
              </div>
            ),
            label: !isTimelineView ? (
              <div className="flex items-center gap-2">
                <ServiceRequestTaskLabel label="Upload your PAN document" />
                <Tooltip content="The file is password-protected. Use your entity's Date of Incorporation in DDMMYYYY format (numbers only).">
                  <Icons.InfoCircle className="size-4 text-text-secondary cursor-help" />
                </Tooltip>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <ServiceRequestTaskLabel label="Upload your PAN document" />
                {task.status === TaskStatus.TO_DO && (
                  <p className="txt-extra-small-regular text-text-tertiary max-w-[calc(100%-24px)]">
                    The file is password-protected. Use your entity's Date of
                    Incorporation in DDMMYYYY format (numbers only).
                  </p>
                )}
              </div>
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <div className="flex items-center gap-2">
                <Button size="sm" disabled variant="primary">
                  <Icons.Upload01 className="size-4" />
                  Upload
                </Button>
              </div>
            ),
            label: !isTimelineView ? (
              <div className="flex items-center gap-2">
                <ServiceRequestTaskLabel label="Upload your PAN document" />
                <Tooltip content="The file is password-protected. Use your entity's Date of Incorporation in DDMMYYYY format (numbers only).">
                  <Icons.InfoCircle className="size-4 text-text-secondary cursor-help" />
                </Tooltip>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <ServiceRequestTaskLabel label="Upload your PAN document" />
                {task.status === TaskStatus.TO_DO && (
                  <p className="txt-extra-small-regular text-text-tertiary max-w-[calc(100%-24px)]">
                    The file is password-protected. Use your entity's Date of
                    Incorporation in DDMMYYYY format (numbers only).
                  </p>
                )}
              </div>
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={handleViewFile}>
                  View upload
                </Button>
              </div>
            ) : null,

            label: (
              <ServiceRequestTaskLabel
                label="PAN document uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload PAN"
        documentType={LegacyDocumentType.CorporatePAN}
        customComp={
          <div className="text-sm text-tertiary">
            <p>
              <strong>Password Hint:</strong> The file is password-protected.
              Use your entity's Date of Incorporation in DDMMYYYY format
              (numbers only).
            </p>
          </div>
        }
        onUploadSuccess={async () => {
          await updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          });
        }}
      />
    </div>
  );
};
